import { AxiosPromise } from 'axios';

import { http } from 'services';

import * as Types from './types';

// --- Google sign-in (public) ---
export const GoogleSignIn = (
  values: Types.IApi.GoogleSignIn.Request,
): AxiosPromise<Types.IApi.GoogleSignIn.Response> =>
  http.request.post('/api/auth/google', values);

// --- Telegram sign-in (public) ---
export const TelegramSignIn = (
  values: Types.IApi.TelegramSignIn.Request,
): AxiosPromise<Types.IApi.TelegramSignIn.Response> =>
  http.request.post('/api/auth/telegram/widget', values);

// --- Telegram Mini App sign-in (public; initData handshake inside Telegram) ---
export const TelegramMiniAppSignIn = (
  values: Types.IApi.TelegramMiniAppSignIn.Request,
): AxiosPromise<Types.IApi.TelegramMiniAppSignIn.Response> =>
  http.request.post('/api/auth/telegram', values);

// --- Telegram bot-approve web login (public) ---
export const TelegramBotLoginStart =
  (
    accountType?: 'STAFF' | 'CANDIDATE',
    mode?: 'login' | 'signup',
  ): AxiosPromise<Types.IApi.TelegramBotLoginStart.Response> =>
    http.request.post('/api/auth/telegram/login/start', { accountType, mode });

export const TelegramBotLoginPoll = (
  requestId: string,
): AxiosPromise<Types.IApi.TelegramBotLoginPoll.Response> =>
  http.request.get('/api/auth/telegram/login/poll', { params: { requestId } });

// --- Phone-first signup (public) ---
export const StartPhoneRegister = (
  values: Types.IApi.StartPhoneRegister.Request,
): AxiosPromise<Types.IApi.StartPhoneRegister.Response> =>
  http.request.post('/api/auth/register/phone/start', values);

export const ResendPhoneRegister = (
  values: Types.IApi.PhoneChallenge.Request,
): AxiosPromise<Types.IApi.PhoneChallenge.ExpiryResponse> =>
  http.request.post('/api/auth/register/phone/resend', values);

export const VerifyPhoneRegister = (
  values: Types.IApi.VerifyPhoneRegister.Request,
): AxiosPromise<Types.IApi.VerifyPhoneRegister.Response> =>
  http.request.post('/api/auth/register/phone/verify', values);

export const CompletePhoneRegister = (
  values: Types.IApi.CompletePhoneRegister.Request,
): AxiosPromise<Types.IApi.AuthResult.Response> =>
  http.request.post('/api/auth/register/phone/complete', values);

// --- Onboarding ("moderation") gate (authenticated) ---
export const CompleteOnboarding = (
  values: Types.IApi.CompleteOnboarding.Request,
): AxiosPromise<Types.IApi.CompleteOnboarding.Response> =>
  http.request.post('/api/auth/onboarding/complete', values);

export const CompleteLearnerOnboarding = (
  values: Types.IApi.CompleteLearnerOnboarding.Request,
): AxiosPromise<Types.IApi.CompleteLearnerOnboarding.Response> =>
  http.request.post('/api/auth/onboarding/learner', values);

export const CompleteExternalAccountType = (
  values: Types.IApi.CompleteExternalAccountType.Request,
): AxiosPromise<Types.IApi.CompleteExternalAccountType.Response> =>
  http.request.post('/api/auth/account-type/complete', values);

// --- Phone + password login (public) ---
export const PhoneLogin = (
  values: Types.IApi.PhoneLogin.Request,
): AxiosPromise<Types.IApi.AuthResult.Response> =>
  http.request.post('/api/auth/login/phone', values);

// --- Phone-OTP password recovery (public) ---
export const StartPhoneRecover = (
  values: Types.IApi.StartPhoneRecover.Request,
): AxiosPromise<Types.IApi.StartPhoneRecover.Response> =>
  http.request.post('/api/auth/recover/phone/start', values);

export const ResendPhoneRecover = (
  values: Types.IApi.PhoneChallenge.Request,
): AxiosPromise<Types.IApi.PhoneChallenge.ExpiryResponse> =>
  http.request.post('/api/auth/recover/phone/resend', values);

export const CompletePhoneRecover = (
  values: Types.IApi.CompletePhoneRecover.Request,
): AxiosPromise<Types.IApi.CompletePhoneRecover.Response> =>
  http.request.post('/api/auth/recover/phone/complete', values);

// --- Session lifecycle ---
export const Refresh = (
  values: Types.IApi.Refresh.Request
): AxiosPromise<Types.IApi.Refresh.Response> =>
  http.request.post('/api/auth/refresh', values);

export const Profile = (): AxiosPromise<Types.IApi.Profile.Response> =>
  http.request.get('/api/auth/profile');

// Resolved permission keys for the caller's active org (X-Organization-Id header).
// Sourced separately from the profile to keep that payload thin.
export const MyPermissions = (): AxiosPromise<Types.IApi.MyPermissions.Response> =>
  http.request.get('/api/auth/me/permissions');

export const Logout = (): AxiosPromise => http.request.post('/api/auth/logout');

// --- Active sessions (account settings) ---
export const GetSessions = (): AxiosPromise<Types.IApi.Sessions.Response> =>
  http.request.get('/api/auth/sessions');

// Revoke one session by id (sign out that device).
export const RevokeSession = (id: string): AxiosPromise =>
  http.request.delete(`/api/auth/sessions/${id}`);

// Revoke every session except the current one (sign out all other devices).
export const RevokeOtherSessions = (): AxiosPromise<{ message: string; count: number }> =>
  http.request.delete('/api/auth/sessions');

export const SwitchOrganization = (
  values: Types.IApi.SwitchOrganization.Request
): AxiosPromise<Types.IApi.SwitchOrganization.Response> =>
  http.request.post('/api/auth/switch-organization', values);

export const UpdateProfile = (values: {
  name?: string;
  avatarUrl?: string;
  tourDismissed?: boolean;
}): AxiosPromise => http.request.patch('/api/users/me', values);

export const UploadAvatar = (file: File): AxiosPromise<{ avatarUrl: string }> => {
  const form = new FormData();
  form.append('file', file);
  return http.request.post('/api/users/me/avatar', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const ChangePassword = (values: {
  currentPassword: string;
  newPassword: string;
}): AxiosPromise =>
  http.request.post('/api/users/me/change-password', values);

// --- Optional email for phone users (authenticated) ---
export const AddEmail = (
  values: Types.IApi.AddEmail.Request,
): AxiosPromise<Types.IApi.PhoneChallenge.ExpiryResponse> =>
  http.request.post('/api/auth/email/add', values);

export const ResendEmailVerification = (): AxiosPromise<Types.IApi.ResendEmailVerification.Response> =>
  http.request.post('/api/auth/email/resend');

export const VerifyEmail = (
  values: Types.IApi.VerifyEmail.Request,
): AxiosPromise<Types.IApi.VerifyEmail.Response> =>
  http.request.post('/api/auth/email/verify', values);

// --- Phone setup (authenticated, from settings) ---
export const StartPhoneSetup = (
  values: Types.IApi.StartPhoneSetup.Request,
): AxiosPromise<Types.IApi.StartPhoneSetup.Response> =>
  http.request.post('/api/users/me/phone/start', values);

export const VerifyPhoneSetup = (
  values: Types.IApi.VerifyPhoneSetup.Request,
): AxiosPromise<Types.IApi.VerifyPhoneSetup.Response> =>
  http.request.post('/api/users/me/phone/verify', values);

export const RemovePhone = (): AxiosPromise<Types.IApi.RemovePhone.Response> =>
  http.request.delete('/api/users/me/phone');

export interface AccountDeletionPreflight {
  canSchedule: boolean;
  ownedOrganizations: Array<{ id: string; name: string; slug: string; subscriptionStatus?: string }>;
  membershipCount: number;
  retainedRecords: string;
}

export const GetAccountDeletionPreflight = (): AxiosPromise<AccountDeletionPreflight> =>
  http.request.get('/api/users/me/account-deletion/preflight');

export const StartAccountDeletion = (): AxiosPromise<{ expiresAt: string; delivery: { channel: string; destinationHint: string } }> =>
  http.request.post('/api/users/me/account-deletion/verification');

export const ConfirmAccountDeletion = (code: string): AxiosPromise<{ scheduledFor: string; recoveryToken: string }> =>
  http.request.post('/api/users/me/account-deletion', { code });

export const CancelAccountDeletion = (recoveryToken: string): AxiosPromise<{ message: string }> =>
  http.request.post('/api/users/account-deletion/cancel', { recoveryToken });
